import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import type { FC } from 'react'
import { getQueryOptions } from '../../../services'
import type { ClientOutputs } from '@botpress/client'
import { useUsage } from '../../../hooks'
import { DYNAMIC_QUOTAS } from '../../usage/constants'
import { quotaTypes } from '@bpinternal/const'
import { Bot } from 'lucide-react'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { useNavigate } from '@tanstack/react-router'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@bpinternal/ui-kit-next'

export const BotsUsage: FC<{ workspaceId: string }> = ({ workspaceId }) => {
  const navigate = useNavigate()
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data

  const includedQuotas: ClientOutputs['getUsage']['usage']['type'][] = ['bot_count']

  const usagesRecord = useUsage({
    workspaceId: workspace.id,
    quotas: [...DYNAMIC_QUOTAS, ...quotaTypes].filter((q) => includedQuotas.includes(q)),
  })

  const usages = [...Object.values(usagesRecord)].sort((a, b) => b.percentage - a.percentage)

  return (
    <Card className="bg-card-filled border-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Bot className="h-5 w-5" />
          Bots
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/workspaces/$workspaceId/billing', params: { workspaceId } })}
        >
          Manage
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {usages.map((usage) => (
          <div key={usage.type} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-nowrap text-muted-foreground">
                <span>{formatValueWithUnit(usage.value, usage.type)}</span>
                <span>{` of ${formatValueWithUnit(usage.quota, usage.type)} used`}</span>
              </span>
              <span className="text-muted-foreground">{usage.percentage.toFixed(0)}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-primary transition-all duration-300"
                style={{ width: `${Math.min(usage.percentage, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
